import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Building,
  Users,
  Heart,
  Shield,
  Home,
  FileText,
  CheckCircle,
  Clock,
} from "lucide-react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { supabase } from "../../integrations/supabase/client";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

interface Stats {
  totalHostels: number;
  pendingHostels: number;
  approvedHostels: number;
  totalInstitutions: number;
  pendingInstitutions: number;
  totalStudents: number;
  totalDonors: number;
  totalTrustees: number;
  totalDonations: number;
}

interface PendingItem {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  created_at: string | null;
  type: "hostel" | "institution";
}

export default function SuperAdminDashboard() {
  const navigate = usePrefixedNavigate();
  const [stats, setStats] = useState<Stats>({
    totalHostels: 0,
    pendingHostels: 0,
    approvedHostels: 0,
    totalInstitutions: 0,
    pendingInstitutions: 0,
    totalStudents: 0,
    totalDonors: 0,
    totalTrustees: 0,
    totalDonations: 0,
  });
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch hostels stats
      const { data: hostels } = await supabase
        .from("hostels")
        .select("id, status");
      const totalHostels = hostels?.length ?? 0;
      const pendingHostelsCount =
        hostels?.filter(h => h.status === "pending").length ?? 0;
      const approvedHostels =
        hostels?.filter(h => h.status === "approved").length ?? 0;

      // Fetch institutions stats
      const { data: institutions } = await supabase
        .from("institutions")
        .select("id, status");
      const totalInstitutions = institutions?.length ?? 0;
      const pendingInstitutions =
        institutions?.filter(i => i.status === "pending").length ?? 0;

      // Fetch pending hostels for quick action
      const { data: pendingHostelsList } = await supabase
        .from("hostels")
        .select("id, name, city, state, created_at")
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(3);

      // Fetch pending institutions
      const { data: pendingInstitutionsList } = await supabase
        .from("institutions")
        .select("id, name, city, state, created_at")
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(3);

      // Combine pending items
      const allPending: PendingItem[] = [
        ...(pendingHostelsList?.map(h => ({ ...h, type: "hostel" as const })) ??
          []),
        ...(pendingInstitutionsList?.map(i => ({
          ...i,
          type: "institution" as const,
        })) ?? []),
      ]
        .sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 5);

      // Fetch other stats
      const { count: studentsCount } = await supabase
        .from("students")
        .select("*", { count: "exact", head: true });
      const { count: donorsCount } = await supabase
        .from("donors")
        .select("*", { count: "exact", head: true });
      const { count: trusteesCount } = await supabase
        .from("trustees")
        .select("*", { count: "exact", head: true });

      // Fetch total donations
      const { data: donations } = await supabase
        .from("donations")
        .select("amount");
      const totalDonations =
        donations?.reduce((sum, d) => sum + (d.amount ?? 0), 0) ?? 0;

      setStats({
        totalHostels,
        pendingHostels: pendingHostelsCount,
        approvedHostels,
        totalInstitutions,
        pendingInstitutions,
        totalStudents: studentsCount ?? 0,
        totalDonors: donorsCount ?? 0,
        totalTrustees: trusteesCount ?? 0,
        totalDonations,
      });
      setPendingItems(allPending);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      title: "Total Hostels",
      value: stats.totalHostels.toString(),
      icon: Building,
      color: "text-primary",
      path: "/super-admin/hostels",
    },
    {
      title: "Pending Approvals",
      value: (stats.pendingHostels + stats.pendingInstitutions).toString(),
      icon: Clock,
      color: "text-yellow-600",
      path: "/super-admin/hostels",
    },
    {
      title: "Approved Hostels",
      value: stats.approvedHostels.toString(),
      icon: CheckCircle,
      color: "text-green-600",
      path: "/super-admin/hostels",
    },
    {
      title: "Total Institutions",
      value: stats.totalInstitutions.toString(),
      icon: Home,
      color: "text-purple-600",
      path: "/super-admin/institutions",
    },
    {
      title: "Total Students",
      value: stats.totalStudents.toString(),
      icon: Users,
      color: "text-primary",
      path: "/super-admin/user-management",
    },
    {
      title: "Donors",
      value: stats.totalDonors.toString(),
      icon: Heart,
      color: "text-rose-600",
      path: "/super-admin/user-management",
    },
    {
      title: "Trustees",
      value: stats.totalTrustees.toString(),
      icon: Shield,
      color: "text-amber-600",
      path: "/super-admin/user-management",
    },
    {
      title: "Total Donations",
      value: `₹${(stats.totalDonations / 1000).toFixed(0)}K`,
      icon: FileText,
      color: "text-green-600",
      path: "/super-admin/funding",
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="mb-2 text-3xl font-bold text-foreground">
          Super Admin Dashboard
        </h1>
        <p className="text-muted-foreground">
          Complete overview and control of the JBF Portal
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map(card => {
          const Icon = card.icon;
          return (
            <Card
              key={`kpi-${card.title}`}
              className="cursor-pointer border-primary/20 transition-all hover:-translate-y-1 hover:shadow-lg"
              onClick={() => navigate(card.path)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </CardTitle>
                <Icon className={`h-5 w-5 ${card.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">
                  {card.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pending Approvals Section */}
      {pendingItems.length > 0 && (
        <Card className="border-yellow-500/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              Pending Approvals (
              {stats.pendingHostels + stats.pendingInstitutions})
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/super-admin/hostels")}
              >
                Hostels ({stats.pendingHostels})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/super-admin/institutions")}
              >
                Institutions ({stats.pendingInstitutions})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingItems.map(item => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {item.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.city ?? "N/A"}, {item.state ?? "N/A"}
                    </TableCell>
                    <TableCell>
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(
                            item.type === "hostel"
                              ? "/super-admin/hostels"
                              : "/super-admin/institutions"
                          )
                        }
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Overview */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Hostel Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span>Approved</span>
                </div>
                <span className="font-semibold">{stats.approvedHostels}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <span>Pending</span>
                </div>
                <span className="font-semibold">{stats.pendingHostels}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span>Rejected</span>
                </div>
                <span className="font-semibold">
                  {stats.totalHostels -
                    stats.approvedHostels -
                    stats.pendingHostels}
                </span>
              </div>
              <div className="border-t pt-4">
                <Button
                  className="w-full"
                  onClick={() => navigate("/super-admin/hostels")}
                >
                  Manage Hostels
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span>Students</span>
                </div>
                <span className="font-semibold">{stats.totalStudents}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-rose-600" />
                  <span>Donors</span>
                </div>
                <span className="font-semibold">{stats.totalDonors}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <span>Trustees</span>
                </div>
                <span className="font-semibold">{stats.totalTrustees}</span>
              </div>
              <div className="border-t pt-4">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate("/super-admin/user-management")}
                >
                  Manage Users
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* No Pending Items Message */}
      {pendingItems.length === 0 && (
        <Card className="border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="py-8 text-center">
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-400">
              All Caught Up!
            </h3>
            <p className="text-green-600 dark:text-green-500">
              No pending approvals at the moment.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
